import {
  CampaignCreate,
  CampaignDetail,
  CampaignOption,
  CampaignPostCreate,
  CampaignPostSummary,
  CampaignPostUpdate,
  CampaignSummary,
  CampaignUpdate,
  PipelineCreate,
  PipelineSummary,
  PipelineUpdate,
  PlatformSummary,
} from "@/types/opportunity";
import { Page } from "@/types/pagination";
import type {
  PipelineStageSequenceDetail,
  PipelineStageSequenceSave,
} from "@/types/pipeline-sequence";

import { requestData } from "@/api/api-config";

// ============================================================================
// Pipelines
// ============================================================================

export async function fetchPipelines(
  includeArchived = false,
): Promise<PipelineSummary[] | null> {
  return await requestData<PipelineSummary[]>(
    "opportunity/pipeline/list",
    "get",
    includeArchived ? { searchParams: { include_archived: true } } : undefined,
  );
}

// Pipeline saves carry stage automations, so they fail the same structured ways
// a standalone sequence save does — route them through the same error parsing.
export async function createPipeline(
  pipeline: PipelineCreate,
): Promise<PipelineSummary | null> {
  return await callPipelineEndpoint<PipelineSummary>(
    "opportunity/pipeline",
    "post",
    { json: pipeline },
  );
}

export async function updatePipeline(
  pipelineId: string,
  pipeline: PipelineUpdate,
): Promise<PipelineSummary | null> {
  return await callPipelineEndpoint<PipelineSummary>(
    `opportunity/pipeline/${pipelineId}`,
    "put",
    { json: pipeline },
  );
}

export async function archivePipeline(
  pipelineId: string,
): Promise<PipelineSummary | null> {
  return await requestData<PipelineSummary>(
    `opportunity/pipeline/${pipelineId}/archive`,
    "post",
  );
}

export async function restorePipeline(
  pipelineId: string,
): Promise<PipelineSummary | null> {
  return await requestData<PipelineSummary>(
    `opportunity/pipeline/${pipelineId}/restore`,
    "post",
  );
}

// ============================================================================
// Pipeline Stage Sequences
// ============================================================================

export class PipelineRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number | null,
    public readonly code: string | null = null,
    public readonly enrollmentCount: number | null = null,
  ) {
    super(message);
    this.name = "PipelineRequestError";
  }
}

export async function fetchPipelineStageSequence(
  pipelineId: string,
  stageId: string,
): Promise<PipelineStageSequenceDetail | null> {
  return await requestData<PipelineStageSequenceDetail | null>(
    `opportunity/pipeline/${pipelineId}/stage/${stageId}/automation-sequence`,
    "get",
  );
}

export async function savePipelineStageSequence(
  pipelineId: string,
  stageId: string,
  payload: PipelineStageSequenceSave,
): Promise<PipelineStageSequenceDetail | null> {
  return await callPipelineEndpoint<PipelineStageSequenceDetail>(
    `opportunity/pipeline/${pipelineId}/stage/${stageId}/automation-sequence`,
    "put",
    { json: payload },
  );
}

export async function deletePipelineStageSequence(
  pipelineId: string,
  stageId: string,
): Promise<void> {
  await callPipelineEndpoint(
    `opportunity/pipeline/${pipelineId}/stage/${stageId}/automation-sequence`,
    "delete",
  );
}

async function callPipelineEndpoint<T>(
  endpoint: string,
  method: "post" | "put" | "delete",
  options?: { json: unknown },
): Promise<T | null> {
  try {
    return await requestData<T>(endpoint, method, options, false);
  } catch (error: unknown) {
    throw await toPipelineRequestError(error);
  }
}

async function toPipelineRequestError(
  error: unknown,
): Promise<PipelineRequestError> {
  const maybeHttp = error as { response?: Response; message?: string };
  if (!maybeHttp.response) {
    return new PipelineRequestError(
      maybeHttp.message ?? "The request couldn't reach the server.",
      null,
    );
  }

  let detail: unknown;
  try {
    const body = (await maybeHttp.response.clone().json()) as {
      detail?: unknown;
    };
    detail = body?.detail;
  } catch {
    detail = undefined;
  }

  if (typeof detail === "object" && detail !== null) {
    const structured = detail as {
      code?: unknown;
      enrollmentCount?: unknown;
      errors?: unknown;
    };
    const code = typeof structured.code === "string" ? structured.code : null;
    const enrollmentCount =
      typeof structured.enrollmentCount === "number"
        ? structured.enrollmentCount
        : null;
    if (code === "ACTIVE_ENROLLMENT_CONFLICT") {
      const runLabel = enrollmentCount === 1 ? "person is" : "people are";
      return new PipelineRequestError(
        `${enrollmentCount ?? "Some"} ${runLabel} still going through these automations, so they can't be removed yet. You can still edit, add, or remove individual steps.`,
        maybeHttp.response.status,
        code,
        enrollmentCount,
      );
    }
    if (code === "STEP_HAS_ACTIVE_ENROLLMENTS") {
      return new PipelineRequestError(
        "Someone reached a step you removed after you opened the automations. Reload the page and remove it again to choose what happens to them.",
        maybeHttp.response.status,
        code,
      );
    }
    if (code === "INVALID_ROUTING_GRAPH" && Array.isArray(structured.errors)) {
      return new PipelineRequestError(
        `Sequence routing is invalid: ${structured.errors.join(" ")}`,
        maybeHttp.response.status,
        code,
      );
    }
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) =>
        typeof item === "object" && item !== null && "msg" in item
          ? String(item.msg)
          : String(item),
      )
      .filter(Boolean);
    if (messages.length) {
      return new PipelineRequestError(
        messages.join("; "),
        maybeHttp.response.status,
      );
    }
  }

  return new PipelineRequestError(
    typeof detail === "string"
      ? detail
      : `The request failed (${maybeHttp.response.status}).`,
    maybeHttp.response.status,
  );
}

// ============================================================================
// Platforms
// ============================================================================

export async function fetchPlatforms(): Promise<PlatformSummary[] | null> {
  return await requestData<PlatformSummary[]>(
    "opportunity/platform/list",
    "get",
  );
}

// ============================================================================
// Campaigns
// ============================================================================

export async function fetchCampaigns(
  params?: {
    platformId?: string;
    isActive?: boolean;
  },
  catchError = true,
): Promise<Page<CampaignSummary> | null> {
  return await requestData<Page<CampaignSummary>>(
    "opportunity/campaign/list",
    "get",
    {
      searchParams: params,
    },
    catchError,
  );
}

/** Campaigns matched by name, or the one asked for by id, for a picker. */
export async function searchCampaignOptions(
  params: { search?: string; campaignId?: string },
  catchError = true,
): Promise<CampaignOption[] | null> {
  return await requestData<CampaignOption[]>(
    "opportunity/campaign/options",
    "get",
    { searchParams: params },
    catchError,
  );
}

export async function fetchCampaign(
  campaignId: string,
): Promise<CampaignDetail | null> {
  return await requestData<CampaignDetail>(
    `opportunity/campaign/${campaignId}`,
    "get",
  );
}

export async function createCampaign(
  campaign: CampaignCreate,
): Promise<CampaignSummary | null> {
  return await requestData<CampaignSummary>("opportunity/campaign", "post", {
    json: campaign,
  });
}

export async function updateCampaign(
  campaignId: string,
  campaign: CampaignUpdate,
): Promise<CampaignSummary | null> {
  return await requestData<CampaignSummary>(
    `opportunity/campaign/${campaignId}`,
    "put",
    { json: campaign },
  );
}

export async function deleteCampaign(campaignId: string): Promise<void> {
  await requestData(`opportunity/campaign/${campaignId}`, "delete");
}

// ============================================================================
// Campaign Posts
// ============================================================================

export async function fetchAllCampaignPosts(): Promise<
  CampaignPostSummary[] | null
> {
  return await requestData<CampaignPostSummary[]>(
    "opportunity/campaign/post/all",
    "get",
  );
}

export async function fetchCampaignPosts(
  campaignId: string,
): Promise<CampaignPostSummary[] | null> {
  return await requestData<CampaignPostSummary[]>(
    `opportunity/campaign/${campaignId}/post/list`,
    "get",
  );
}

export async function createCampaignPost(
  post: CampaignPostCreate,
): Promise<CampaignPostSummary | null> {
  return await requestData<CampaignPostSummary>(
    `opportunity/campaign/${post.campaignId}/post`,
    "post",
    { json: post },
  );
}

export async function uploadCampaignPostMedia(
  campaignId: string,
  postId: string,
  files: File[],
): Promise<CampaignPostSummary | null> {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  return await requestData<CampaignPostSummary>(
    `opportunity/campaign/${campaignId}/post/${postId}/media`,
    "post",
    { body: formData },
  );
}

export async function updateCampaignPost(
  campaignId: string,
  postId: string,
  post: CampaignPostUpdate,
): Promise<CampaignPostSummary | null> {
  return await requestData<CampaignPostSummary>(
    `opportunity/campaign/${campaignId}/post/${postId}`,
    "put",
    { json: post },
  );
}

export async function deleteCampaignPost(
  campaignId: string,
  postId: string,
): Promise<void> {
  await requestData(
    `opportunity/campaign/${campaignId}/post/${postId}`,
    "delete",
  );
}
